/**
 * Lightbox Gallery Overlay with Clean Edge-to-Edge Full-screen Sliding Track & GPU Acceleration
 * Features:
 * - Clean Edge-to-Edge View (No side arrow buttons cluttering the photo area)
 * - Full-screen Sliding Track that moves in real-time with finger touch (60 FPS GPU)
 * - Seamless Infinite Looping Navigation via Touch Swipe, Thumbnails Strip, and Keyboard Arrow Keys
 * - 2-finger Pinch-to-Zoom (up to 4x) on active photo
 * - Pan / Drag zoomed photos in 360 degrees
 * - Double-tap to toggle zoom (1x <-> 2.5x)
 */

export interface LightboxImage {
  url: string;
  thumbUrl?: string;
  path?: string;
}

export function openLightbox(images: LightboxImage[], initialIndex: number = 0): void {
  if (!images || images.length === 0) return;

  const totalCount = images.length;
  let currentIndex = Math.max(0, Math.min(initialIndex, totalCount - 1));

  // Zoom & Pan state for the active image
  let currentScale = 1;
  let currentX = 0;
  let currentY = 0;

  // Touch tracking state
  let startTouchDistance = 0;
  let startScale = 1;
  let isPinching = false;

  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let initialX = 0;
  let initialY = 0;

  let lastTapTime = 0;
  let isTrackMoving = false;

  // Remove existing lightbox if present
  const existing = document.querySelector('.lightbox-overlay');
  if (existing) {
    existing.remove();
  }

  const overlay = document.createElement('div');
  overlay.className = 'lightbox-overlay';

  overlay.innerHTML = `
    <div class="lightbox-top-bar">
      <span class="lightbox-counter"><span id="lb-current">${currentIndex + 1}</span> / ${totalCount}</span>
      <button class="lightbox-close" aria-label="Close">✕</button>
    </div>

    <div class="lightbox-content">
      <div class="lightbox-stage">
        <div class="lightbox-track">
          ${images
            .map(
              (img, i) => `
            <div class="lightbox-slide" data-index="${i}">
              <img class="lightbox-slide-img" src="${img.url}" alt="Room Image ${i + 1}" draggable="false" />
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    </div>

    ${
      totalCount > 1
        ? `
      <div class="lightbox-thumbs">
        ${images
          .map(
            (img, idx) => `
          <button class="lightbox-thumb-item ${idx === currentIndex ? 'active' : ''}" data-index="${idx}">
            <img src="${img.thumbUrl || img.url}" alt="thumb ${idx + 1}" draggable="false" />
          </button>
        `
          )
          .join('')}
      </div>
    `
        : ''
    }
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  const stageEl = overlay.querySelector('.lightbox-stage') as HTMLElement;
  const trackEl = overlay.querySelector('.lightbox-track') as HTMLElement;
  const slideImgs = Array.from(overlay.querySelectorAll('.lightbox-slide-img')) as HTMLImageElement[];
  const currentEl = overlay.querySelector('#lb-current') as HTMLElement;
  const closeBtn = overlay.querySelector('.lightbox-close') as HTMLElement;
  const thumbBtns = Array.from(overlay.querySelectorAll('.lightbox-thumb-item')) as HTMLElement[];

  const getStageWidth = () => stageEl.clientWidth || window.innerWidth;

  // Apply track sliding position
  const updateTrackPosition = (animate: boolean = true, extraOffsetPx: number = 0) => {
    if (!trackEl) return;
    const stageWidth = getStageWidth();
    const targetX = -currentIndex * stageWidth + extraOffsetPx;

    if (animate) {
      trackEl.style.transition = 'transform 0.3s cubic-bezier(0.25, 1, 0.5, 1)';
    } else {
      trackEl.style.transition = 'none';
    }
    trackEl.style.transform = `translate3d(${targetX}px, 0px, 0px)`;
  };

  // Apply zoom & pan transform to the active image
  const applyImageZoom = (animate: boolean = false) => {
    const activeImg = slideImgs[currentIndex];
    if (!activeImg) return;

    if (animate) {
      activeImg.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
    } else {
      activeImg.style.transition = 'none';
    }
    activeImg.style.transform = `translate3d(${currentX}px, ${currentY}px, 0px) scale(${currentScale})`;
  };

  // Reset zoom & pan on active image
  const resetZoom = (animate: boolean = true) => {
    currentScale = 1;
    currentX = 0;
    currentY = 0;
    applyImageZoom(animate);
  };

  // Update current active slide index
  const updateDisplay = (newIndex: number, animateTrack: boolean = true) => {
    resetZoom(false);
    currentIndex = (newIndex + totalCount) % totalCount;

    updateTrackPosition(animateTrack);

    if (currentEl) {
      currentEl.textContent = (currentIndex + 1).toString();
    }

    thumbBtns.forEach((btn, i) => {
      btn.classList.toggle('active', i === currentIndex);
    });

    const activeThumb = thumbBtns[currentIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  // Set initial position
  requestAnimationFrame(() => {
    updateTrackPosition(false);
  });

  const closeLightbox = () => {
    overlay.classList.add('fade-out');
    setTimeout(() => {
      document.body.style.overflow = '';
      overlay.remove();
    }, 200);
  };

  // Event Listeners
  closeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    closeLightbox();
  });

  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (
      currentScale === 1 &&
      (target.classList.contains('lightbox-overlay') ||
        target.classList.contains('lightbox-content') ||
        target.classList.contains('lightbox-stage'))
    ) {
      closeLightbox();
    }
  });

  thumbBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.getAttribute('data-index') || '0', 10);
      updateDisplay(idx);
    });
  });

  const getTouchDistance = (touches: TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  // Touch Handlers for 60 FPS Real-time Slide Track & Pinch Zoom
  stageEl.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      // 1. Double tap zoom
      const now = Date.now();
      if (e.touches.length === 1 && now - lastTapTime < 300) {
        e.preventDefault();
        if (currentScale > 1.2) {
          resetZoom(true);
        } else {
          currentScale = 2.5;
          currentX = 0;
          currentY = 0;
          applyImageZoom(true);
        }
        lastTapTime = 0;
        return;
      }
      lastTapTime = now;

      // 2. Pinch Zoom start (2 fingers)
      if (e.touches.length === 2) {
        e.preventDefault();
        isPinching = true;
        isTrackMoving = false;
        startTouchDistance = getTouchDistance(e.touches);
        startScale = currentScale;
        return;
      }

      // 3. Track Move or Pan start (1 finger)
      if (e.touches.length === 1) {
        isDragging = true;
        dragStartX = e.touches[0].clientX;
        dragStartY = e.touches[0].clientY;

        if (currentScale > 1.05) {
          initialX = currentX;
          initialY = currentY;
          isTrackMoving = false;
        } else {
          isTrackMoving = true;
        }
      }
    },
    { passive: false }
  );

  stageEl.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      // 1. Handle Pinch Zooming
      if (isPinching && e.touches.length === 2) {
        e.preventDefault();
        const dist = getTouchDistance(e.touches);
        if (startTouchDistance > 0) {
          const newScale = startScale * (dist / startTouchDistance);
          currentScale = Math.max(1, Math.min(newScale, 4));
          applyImageZoom(false);
        }
        return;
      }

      // 2. Handle Real-time 60 FPS Track Sliding (when unzoomed)
      if (isDragging && e.touches.length === 1) {
        const deltaX = e.touches[0].clientX - dragStartX;

        if (currentScale <= 1.05 && isTrackMoving) {
          // Slide track in real-time with finger!
          updateTrackPosition(false, deltaX);
        } else if (currentScale > 1.05) {
          // Pan zoomed image
          e.preventDefault();
          const maxPanX = (stageEl.clientWidth * (currentScale - 1)) / 2;
          const maxPanY = (stageEl.clientHeight * (currentScale - 1)) / 2;

          currentX = Math.max(-maxPanX, Math.min(maxPanX, initialX + deltaX));
          currentY = Math.max(-maxPanY, Math.min(maxPanY, initialY + (e.touches[0].clientY - dragStartY)));
          applyImageZoom(false);
        }
      }
    },
    { passive: false }
  );

  stageEl.addEventListener(
    'touchend',
    (e: TouchEvent) => {
      if (isPinching && e.touches.length < 2) {
        isPinching = false;
        if (currentScale < 1.05) {
          resetZoom(true);
        }
      }

      if (isDragging && e.touches.length === 0) {
        isDragging = false;

        if (currentScale <= 1.05 && isTrackMoving) {
          isTrackMoving = false;
          const endX = e.changedTouches[0].clientX;
          const deltaX = endX - dragStartX;

          if (deltaX < -50) {
            // Swiped left -> next photo smoothly
            updateDisplay(currentIndex + 1, true);
          } else if (deltaX > 50) {
            // Swiped right -> prev photo smoothly
            updateDisplay(currentIndex - 1, true);
          } else {
            // Snap back to active photo smoothly
            updateTrackPosition(true, 0);
          }
        }
      }
    },
    { passive: true }
  );

  // Keyboard navigation for desktop
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeLightbox();
      window.removeEventListener('keydown', handleKeyDown);
    } else if (e.key === 'ArrowRight') {
      updateDisplay(currentIndex + 1);
    } else if (e.key === 'ArrowLeft') {
      updateDisplay(currentIndex - 1);
    }
  };
  window.addEventListener('keydown', handleKeyDown);
}
